import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";

function normalizePlatformKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (p === "morrisons") return "morrison";
  if (p === "sainsburry") return "sainsbury";
  if (p === "sainsbury" || p === "sainsburys") return "sainsbury";
  if (p === "asda" || p === "tesco" || p === "morrison" || p === "sainsbury") {
    return p;
  }
  return "";
}

function toActowizPlatformQueryParam(platform) {
  const key = normalizePlatformKey(platform);
  if (!key) return "";
  // Actowiz returns "sainsburry" as platform; filtered requests expect same.
  if (key === "sainsbury") return "sainsburry";
  return key;
}

function normalizeProvider(p) {
  const images = Array.isArray(p?.all_images)
    ? p.all_images
    : p?.main_image_url
    ? [p.main_image_url]
    : [];
  return {
    platform: normalizePlatformKey(p?.platform),
    pid: p?.product_id,
    sku: p?.sku || p?.product_id,
    title: p?.product_name || p?.sku || "",
    price: p?.price != null ? String(p.price) : "",
    packsize: p?.pack_size || "",
    images,
    description: p?.description || "",
    url: p?.url,
    average_rating: p?.average_rating,
    review_count: p?.review_count,
  };
}

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.\-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildComparison(providers) {
  const order = ["tesco", "morrison", "asda", "sainsbury"];
  const byPlatform = {};

  (Array.isArray(providers) ? providers : []).forEach((provider) => {
    const platform = normalizePlatformKey(provider?.platform);
    if (!platform) return;
    const price = parsePrice(provider?.price);
    if (!Number.isFinite(price)) return;
    const prev = byPlatform[platform];
    if (!Number.isFinite(prev) || price < prev) byPlatform[platform] = price;
  });

  return order.map((platform) => ({
    platform,
    price: Number.isFinite(byPlatform[platform]) ? byPlatform[platform] : null,
  }));
}

function getSearchRequestKey(query, start = 0, offset = 10, limit = offset) {
  const q = String(query || "").trim().toLowerCase();
  return `search:${encodeURIComponent(q)}:${start}:${offset}:${limit}`;
}

function pickBestProvider(providers) {
  if (!Array.isArray(providers) || providers.length === 0) return null;
  let best = providers[0];
  let bestPrice = parsePrice(best?.price);

  for (const p of providers) {
    const pr = parsePrice(p?.price);
    if (Number.isFinite(pr) && (!Number.isFinite(bestPrice) || pr < bestPrice)) {
      best = p;
      bestPrice = pr;
    }
  }
  return best;
}

export const fetchActowizSearch = createAsyncThunk(
  "actowiz/fetchSearch",
  async (
    { query, start = 0, offset = 10, limit, mode = "replace" } = {},
    thunkApi
  ) => {
    const pageLimit = typeof limit === "number" ? limit : offset;
    const qs = new URLSearchParams({
      query: String(query || ""),
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });

    const res = await apiRequest(`api/actowiz/search?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load search results."
      );
    }

    const results = Array.isArray(res.data?.results) ? res.data.results : [];
    // Actowiz may return total in different keys; handle common variants.
    const total =
      typeof res.data?.total === "number"
        ? res.data.total
        : typeof res.data?.count === "number"
        ? res.data.count
        : typeof res.data?.records === "number"
        ? res.data.records
        : typeof res.data?.num_results === "number"
        ? res.data.num_results
        : null;

    return {
      query,
      start,
      offset,
      mode,
      limit: pageLimit,
      total,
      items: results
        .map((r) => {
          const providers = Array.isArray(r?.providers) ? r.providers : null;
          const best = providers ? pickBestProvider(providers) : null;
          const providerLike = best || r;
          return normalizeProvider(providerLike);
        })
        .filter((x) => x && x.title)
        .slice(0, pageLimit),
    };
  },
  {
    condition: (
      { query, start = 0, offset = 10, limit, force } = {},
      { getState }
    ) => {
      if (!String(query || "").trim()) return false;
      if (force) return true;
      const pageLimit = typeof limit === "number" ? limit : offset;
      const key = getSearchRequestKey(query, start, offset, pageLimit);
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizProducts = createAsyncThunk(
  "actowiz/fetchProducts",
  async ({ platform, start = 0, offset = 10, mode = "append" } = {}, thunkApi) => {
    const normalizedPlatform = normalizePlatformKey(platform);
    const platformQuery = toActowizPlatformQueryParam(platform);
    const qs = new URLSearchParams();
    if (platformQuery) qs.set("platform", platformQuery);
    qs.set("start", String(start ?? 0));
    qs.set("offset", String(offset ?? 10));
    const res = await apiRequest(`api/actowiz/products?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load products."
      );
    }

    // Actowiz payloads come in multiple shapes depending on endpoint/version:
    // - { results: [ { providers: [...] }, ... ] }
    // - [ { platform, results: [ <provider-like product>, ... ] }, ... ]
    // - [ <provider-like product>, ... ]
    const data = res.data;
    const results = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
      ? data
      : [];

    const providers = (() => {
      // Shape: [ { platform, results: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.results)) {
        return results.flatMap((group) => (Array.isArray(group?.results) ? group.results : []));
      }
      // Shape: [ { providers: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.providers)) {
        return results.flatMap((r) => (Array.isArray(r?.providers) ? r.providers : []));
      }
      // Shape: [ <provider-like product>, ... ]
      return results;
    })();

    return {
      platform: normalizedPlatform,
      start,
      offset,
      mode,
      items: providers.map(normalizeProvider).filter((x) => x.title),
    };
  },
  {
    condition: ({ platform, start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const normalizedPlatform = normalizePlatformKey(platform);
      const key = `${normalizedPlatform || "all"}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizFeatures = createAsyncThunk(
  "actowiz/fetchFeatures",
  async ({ start = 0, offset = 10, mode = "append" } = {}, thunkApi) => {
    const qs = new URLSearchParams({
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });
    const res = await apiRequest(`api/actowiz/features?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load features."
      );
    }

    const results = Array.isArray(res.data?.results) ? res.data.results : [];

    // Important: features `offset` is based on `results` records (SKUs),
    // but each result can have many `providers` (one per retailer).
    // UI should render 1 card per `result`, otherwise card count != pagination.
    return {
      start,
      offset,
      mode,
      items: results
        .map((r) => {
          const providers = Array.isArray(r?.providers) ? r.providers : [];
          const normalizedProviders = providers.map(normalizeProvider);
          const best = pickBestProvider(normalizedProviders);
          if (!best) return null;
          return {
            ...best,
            comparison: buildComparison(normalizedProviders),
          };
        })
        .filter((x) => x && x.title)
        .slice(0, offset),
    };
  },
  {
    condition: ({ start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const key = `features_v2:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizTopDeals = createAsyncThunk(
  "actowiz/fetchTopDeals",
  async ({ category_list, start = 0, offset = 10, mode = "append" } = {}, thunkApi) => {
    const qs = new URLSearchParams({
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });
    if (String(category_list || "").trim()) qs.set("category_list", String(category_list));

    const res = await apiRequest(`api/actowiz/top_deals?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load top deals."
      );
    }

    const categories = Array.isArray(res.data) ? res.data : [];
    const providers = categories.flatMap((c) =>
      Array.isArray(c?.providers) ? c.providers : []
    );

    const groupedBySku = new Map();
    providers.forEach((provider) => {
      const normalized = normalizeProvider(provider);
      const key = normalized.pid || normalized.sku || "";
      if (!key || !normalized.title) return;
      if (!groupedBySku.has(key)) groupedBySku.set(key, []);
      groupedBySku.get(key).push(normalized);
    });

    const items = Array.from(groupedBySku.values())
      .map((group) => {
        const best = pickBestProvider(group);
        if (!best) return null;
        return {
          ...best,
          comparison: buildComparison(group),
        };
      })
      .filter((x) => x && x.title)
      .slice(0, offset);

    return {
      category_list: category_list || "",
      start,
      offset,
      mode,
      items,
    };
  },
  {
    condition: ({ category_list, start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const catKey = String(category_list || "").trim().toLowerCase();
      const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

const initialState = {
  byPlatform: {
    tesco: [],
    asda: [],
    morrison: [],
    sainsbury: [],
  },
  features: [],
  featuresStatus: "idle", // idle | loading | succeeded | failed
  featuresError: "",
  topDeals: [],
  topDealsStatus: "idle", // idle | loading | succeeded | failed
  topDealsError: "",
  search: [],
  searchStatus: "idle", // idle | loading | succeeded | failed
  searchError: "",
  searchTotal: null,
  statusByPlatform: {
    tesco: "idle",
    asda: "idle",
    morrison: "idle",
    sainsbury: "idle",
  },
  errorByPlatform: {
    tesco: "",
    asda: "",
    morrison: "",
    sainsbury: "",
  },
  // track pagination requests to avoid duplicate calls
  requests: {},
};

const actowizSlice = createSlice({
  name: "actowiz",
  initialState,
  reducers: {
    clearActowiz(state) {
      state.byPlatform = { tesco: [], asda: [], morrison: [], sainsbury: [] };
      state.features = [];
      state.featuresStatus = "idle";
      state.featuresError = "";
      state.topDeals = [];
      state.topDealsStatus = "idle";
      state.topDealsError = "";
      state.search = [];
      state.searchStatus = "idle";
      state.searchError = "";
      state.searchTotal = null;
      state.statusByPlatform = {
        tesco: "idle",
        asda: "idle",
        morrison: "idle",
        sainsbury: "idle",
      };
      state.errorByPlatform = { tesco: "", asda: "", morrison: "", sainsbury: "" };
      state.requests = {};
    },
    clearActowizFeatures(state) {
      state.features = [];
      state.featuresStatus = "idle";
      state.featuresError = "";
      // Clear only features pagination request markers
      Object.keys(state.requests).forEach((k) => {
        if (k.startsWith("features_v2:")) delete state.requests[k];
      });
    },
    clearActowizRequest(state, action) {
      const key = String(action.payload || "");
      if (key) delete state.requests[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActowizProducts.pending, (state, action) => {
        const { platform, start = 0, offset = 10 } = action.meta.arg || {};
        const normalizedPlatform = normalizePlatformKey(platform);
        const key = `${normalizedPlatform || "all"}:${start}:${offset}`;
        state.requests[key] = "loading";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "loading";
          state.errorByPlatform[normalizedPlatform] = "";
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "loading";
            state.errorByPlatform[k] = "";
          });
        }
      })
      .addCase(fetchActowizProducts.fulfilled, (state, action) => {
        const { platform, start = 0, offset = 10, items, mode } = action.payload || {};
        const key = `${platform || "all"}:${start}:${offset}`;
        state.requests[key] = "succeeded";
        if (platform) {
          state.statusByPlatform[platform] = "succeeded";
          state.errorByPlatform[platform] = "";
          if (mode === "replace") {
            state.byPlatform[platform] = items || [];
          } else {
            // Append mode
            if (start === 0) state.byPlatform[platform] = items || [];
            else state.byPlatform[platform] = [...(state.byPlatform[platform] || []), ...(items || [])];
          }
        } else {
          const grouped = { tesco: [], asda: [], morrison: [], sainsbury: [] };
          (items || []).forEach((item) => {
            const p = normalizePlatformKey(item?.platform);
            if (p) grouped[p].push(item);
          });
          Object.keys(grouped).forEach((k) => {
            state.statusByPlatform[k] = "succeeded";
            state.errorByPlatform[k] = "";
            if (mode === "replace") state.byPlatform[k] = grouped[k];
            else if (start === 0) state.byPlatform[k] = grouped[k];
            else state.byPlatform[k] = [...(state.byPlatform[k] || []), ...grouped[k]];
          });
        }
      })
      .addCase(fetchActowizProducts.rejected, (state, action) => {
        const { platform, start = 0, offset = 10 } = action.meta.arg || {};
        const normalizedPlatform = normalizePlatformKey(platform);
        const key = `${normalizedPlatform || "all"}:${start}:${offset}`;
        state.requests[key] = "failed";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "failed";
          state.errorByPlatform[normalizedPlatform] =
            typeof action.payload === "string" ? action.payload : "Failed to load products.";
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "failed";
            state.errorByPlatform[k] =
              typeof action.payload === "string" ? action.payload : "Failed to load products.";
          });
        }
      })
      .addCase(fetchActowizFeatures.pending, (state, action) => {
        const { start = 0, offset = 10 } = action.meta.arg || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "loading";
        state.featuresStatus = "loading";
        state.featuresError = "";
      })
      .addCase(fetchActowizFeatures.fulfilled, (state, action) => {
        const { start = 0, offset = 10, items, mode } = action.payload || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "succeeded";
        state.featuresStatus = "succeeded";
        state.featuresError = "";
        if (mode === "replace") state.features = items || [];
        else {
          if (start === 0) state.features = items || [];
          else state.features = [...(state.features || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizFeatures.rejected, (state, action) => {
        const { start = 0, offset = 10 } = action.meta.arg || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "failed";
        state.featuresStatus = "failed";
        state.featuresError =
          typeof action.payload === "string" ? action.payload : "Failed to load features.";
      });

    builder
      .addCase(fetchActowizTopDeals.pending, (state, action) => {
        const { category_list, start = 0, offset = 10 } = action.meta.arg || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "loading";
        state.topDealsStatus = "loading";
        state.topDealsError = "";
      })
      .addCase(fetchActowizTopDeals.fulfilled, (state, action) => {
        const { category_list, start = 0, offset = 10, items, mode } = action.payload || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "succeeded";
        state.topDealsStatus = "succeeded";
        state.topDealsError = "";
        if (mode === "replace") state.topDeals = items || [];
        else {
          if (start === 0) state.topDeals = items || [];
          else state.topDeals = [...(state.topDeals || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizTopDeals.rejected, (state, action) => {
        const { category_list, start = 0, offset = 10 } = action.meta.arg || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "failed";
        state.topDealsStatus = "failed";
        state.topDealsError =
          typeof action.payload === "string" ? action.payload : "Failed to load top deals.";
      });

    builder
      .addCase(fetchActowizSearch.pending, (state, action) => {
        const { query, start = 0, offset = 10, limit } = action.meta.arg || {};
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "loading";
        state.searchStatus = "loading";
        state.searchError = "";
        state.searchTotal = null;
      })
      .addCase(fetchActowizSearch.fulfilled, (state, action) => {
        const {
          start = 0,
          offset = 10,
          mode,
          items,
          query,
          total,
          limit: payloadLimit,
        } = action.payload || {};
        const pageLimit =
          typeof payloadLimit === "number" ? payloadLimit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "succeeded";
        state.searchStatus = "succeeded";
        state.searchError = "";
        state.searchTotal = typeof total === "number" ? total : state.searchTotal;
        if (mode === "replace") state.search = items || [];
        else {
          if (start === 0) state.search = items || [];
          else state.search = [...(state.search || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizSearch.rejected, (state, action) => {
        const { query, start = 0, offset = 10, limit } = action.meta.arg || {};
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "failed";
        state.searchStatus = "failed";
        state.searchError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load search results.";
      });
  },
});

export const { clearActowiz, clearActowizFeatures, clearActowizRequest } = actowizSlice.actions;
export default actowizSlice.reducer;

